import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker, Switch } from 'antd';
import { Calendar, Clock, UserCheck, Sun, Sunset, Lock } from 'lucide-react';
import dayjs from 'dayjs';
import apiClient from '../../utils/apiClient';
import { showToast } from '../../utils/toastNotification';

export const STANDARD_MORNING_SLOTS = [
  { id: 'm1', startTime: '07:30', endTime: '08:00', label: '07:30 - 08:00', key: '07:30-08:00' },
  { id: 'm2', startTime: '08:00', endTime: '08:30', label: '08:00 - 08:30', key: '08:00-08:30' },
  { id: 'm3', startTime: '08:30', endTime: '09:00', label: '08:30 - 09:00', key: '08:30-09:00' },
  { id: 'm4', startTime: '09:00', endTime: '09:30', label: '09:00 - 09:30', key: '09:00-09:30' },
  { id: 'm5', startTime: '09:30', endTime: '10:00', label: '09:30 - 10:00', key: '09:30-10:00' },
  { id: 'm6', startTime: '10:00', endTime: '10:30', label: '10:00 - 10:30', key: '10:00-10:30' },
  { id: 'm7', startTime: '10:30', endTime: '11:00', label: '10:30 - 11:00', key: '10:30-11:00' },
  { id: 'm8', startTime: '11:00', endTime: '11:30', label: '11:00 - 11:30', key: '11:00-11:30' },
];

export const STANDARD_AFTERNOON_SLOTS = [
  { id: 'a1', startTime: '13:30', endTime: '14:00', label: '13:30 - 14:00', key: '13:30-14:00' },
  { id: 'a2', startTime: '14:00', endTime: '14:30', label: '14:00 - 14:30', key: '14:00-14:30' },
  { id: 'a3', startTime: '14:30', endTime: '15:00', label: '14:30 - 15:00', key: '14:30-15:00' },
  { id: 'a4', startTime: '15:00', endTime: '15:30', label: '15:00 - 15:30', key: '15:00-15:30' },
  { id: 'a5', startTime: '15:30', endTime: '16:00', label: '15:30 - 16:00', key: '15:30-16:00' },
  { id: 'a6', startTime: '16:00', endTime: '16:30', label: '16:00 - 16:30', key: '16:00-16:30' },
  { id: 'a7', startTime: '16:30', endTime: '17:00', label: '16:30 - 17:00', key: '16:30-17:00' },
];

export const ALL_STANDARD_SLOTS = [...STANDARD_MORNING_SLOTS, ...STANDARD_AFTERNOON_SLOTS];

const getDayOfWeek = (dateString) => {
  if (!dateString) return '---';
  try {
    const days = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
    const d = dayjs(dateString);
    if (!d.isValid()) return '---';
    const dayIndex = d.day();
    return days[dayIndex] || '---';
  } catch (e) {
    return '---';
  }
};

export default function AddLeaderSchedule() {
  const currentUser = useSelector((state) => state.auth?.user);
  const currentUserId = currentUser?.userId;

  const [selectedDate, setSelectedDate] = useState(dayjs().add(1, 'day'));
  const [selectedSlotKeys, setSelectedSlotKeys] = useState(new Set());
  const [bookedSlotKeys, setBookedSlotKeys] = useState(new Set());
  const [schedulesSummary, setSchedulesSummary] = useState([]);
  const [currentDaySchedule, setCurrentDaySchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [togglingSlot, setTogglingSlot] = useState(null);

  // Chuỗi ngày YYYY-MM-DD đang chọn
  const selectedDateStr = useMemo(() => {
    return selectedDate ? selectedDate.format('YYYY-MM-DD') : '';
  }, [selectedDate]);

  // Tải danh sách tổng quan lịch (chỉ lấy lịch của chính lãnh đạo đang đăng nhập)
  const fetchSchedulesSummary = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/leader-meeting-schedules/management', {
        params: { 
          size: 100,
          leaderId: currentUserId || undefined
        }
      });
      const list = res.data?.data || res.data || [];
      const arrayList = Array.isArray(list) ? list : [];
      const mySchedules = currentUserId
        ? arrayList.filter(item => !item.leader?.id || String(item.leader?.id) === String(currentUserId))
        : arrayList;
      setSchedulesSummary(mySchedules);
      return mySchedules;
    } catch (error) {
      console.error('Lỗi khi tải danh sách lịch:', error);
      return [];
    }
  }, [currentUserId]);

  // Tải chi tiết các ca của ngày đang chọn & map chuẩn vào 15 ca 30p
  const loadDaySlots = useCallback(async (dateStr, summaryList) => {
    if (!dateStr) return;
    const list = summaryList || schedulesSummary;
    const existing = list.find(item => {
      const d = item.receptionDate ? (String(item.receptionDate).includes('T') ? String(item.receptionDate).split('T')[0] : String(item.receptionDate)) : '';
      const isMyLeader = !currentUserId || !item.leader?.id || String(item.leader?.id) === String(currentUserId);
      return d === dateStr && isMyLeader;
    });

    if (existing && existing.id) {
      try {
        setLoading(true);
        const res = await apiClient.get(`/api/leader-meeting-schedules/management/${existing.id}`);
        const detail = res.data?.data || res.data || {};
        setCurrentDaySchedule(detail);

        const activeKeys = new Set();
        const bookedKeys = new Set();

        if (Array.isArray(detail.slots)) {
          detail.slots.forEach(s => {
            if (s.isActive !== false) {
              const dbStart = s.startTime || '09:00';
              const dbEnd = s.endTime || '10:30';
              const isSlotBooked = s.heldCount > 0 || (s.capacity && s.remainingCapacity !== undefined && s.remainingCapacity < s.capacity);

              ALL_STANDARD_SLOTS.forEach(std => {
                if (std.startTime < dbEnd && std.endTime > dbStart) {
                  activeKeys.add(std.key);
                  if (isSlotBooked) {
                    bookedKeys.add(std.key);
                  }
                }
              });
            }
          });
        }

        setSelectedSlotKeys(activeKeys);
        setBookedSlotKeys(bookedKeys);
      } catch (err) {
        console.error('Lỗi khi tải chi tiết ngày:', err);
        setSelectedSlotKeys(new Set());
        setBookedSlotKeys(new Set());
        setCurrentDaySchedule(null);
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedSlotKeys(new Set());
      setBookedSlotKeys(new Set());
      setCurrentDaySchedule(null);
    }
  }, [schedulesSummary, currentUserId]);

  // Khởi chạy khi mount
  useEffect(() => {
    (async () => {
      const list = await fetchSchedulesSummary();
      loadDaySlots(selectedDateStr, list);
    })();
  }, [fetchSchedulesSummary]);

  // Khi người dùng chọn ngày khác trên DatePicker
  useEffect(() => {
    loadDaySlots(selectedDateStr, schedulesSummary);
  }, [selectedDateStr, loadDaySlots, schedulesSummary]);

  // Tập hợp các ngày đã có lịch (chỉ lấy từ hôm nay trở đi) để highlight trên Lịch DatePicker
  const scheduledDateSet = useMemo(() => {
    if (!Array.isArray(schedulesSummary)) return new Set();
    const todayStr = dayjs().format('YYYY-MM-DD');
    return new Set(
      schedulesSummary
        .filter(item => item.isActive !== false && (item.slotCount > 0 || (Array.isArray(item.slots) && item.slots.length > 0)))
        .map(item => {
          const d = item.receptionDate ? (String(item.receptionDate).includes('T') ? String(item.receptionDate).split('T')[0] : String(item.receptionDate)) : '';
          return d;
        })
        .filter(d => d && d >= todayStr)
    );
  }, [schedulesSummary]);

  const isSlotPassed = useCallback((slot) => {
    if (!selectedDate) return false;
    const isToday = selectedDate.isSame(dayjs(), 'day');
    if (!isToday) return false;
    const currentTime = dayjs().format('HH:mm');
    return slot.startTime <= currentTime;
  }, [selectedDate]);

  // Thao tác Bật / Tắt 1 ca (Lưu tự động tức thì)
  const handleToggleSlot = async (slot) => {
    if (togglingSlot) return; 

    // Không cho thao tác trên ca đã qua giờ bắt đầu
    if (isSlotPassed(slot)) {
      showToast.warning(`Ca ${slot.label} đã qua giờ tiếp dân (không thể chỉnh sửa)!`);
      return;
    }
    
    const slotKey = slot.key;
    const isCurrentlyActive = selectedSlotKeys.has(slotKey);

    // Nếu ca này đã qua thời gian tiếp dân thì không cho mở
    const todayStr = dayjs().format('YYYY-MM-DD');
    const currentTimeStr = dayjs().format('HH:mm');
    const isPast = selectedDateStr < todayStr || (selectedDateStr === todayStr && slot.startTime <= currentTimeStr);

    if (!isCurrentlyActive && isPast) {
      showToast.warning(`Không thể mở ca ${slot.label} vì đã qua thời gian tiếp dân!`);
      return;
    }

    // Nếu ca này đã có người đăng ký thì không cho tắt
    if (isCurrentlyActive && bookedSlotKeys.has(slotKey)) {
      showToast.warning(`Ca ${slot.label} đã có công dân nộp đơn đăng ký tiếp dân (không thể tắt)!`);
      return;
    }

    // Danh sách slot mới
    const nextSlotKeys = new Set(selectedSlotKeys);
    if (isCurrentlyActive) {
      nextSlotKeys.delete(slotKey);
    } else {
      nextSlotKeys.add(slotKey);
    }

    // Chuyển thành payload mảng các ca 30p chuẩn
    const standardKeys = new Set(ALL_STANDARD_SLOTS.map(s => s.key));
    const slotsPayload = Array.from(nextSlotKeys)
      .filter(key => standardKeys.has(key))
      .map(key => {
        const [startTime, endTime] = key.split('-');
        return { startTime, endTime };
      })
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    setTogglingSlot({ key: slotKey, action: isCurrentlyActive ? 'CLOSING' : 'OPENING' });
    setSelectedSlotKeys(nextSlotKeys);

    try {
      const existingId = currentDaySchedule?.id || schedulesSummary.find(item => {
        const d = item.receptionDate ? (String(item.receptionDate).includes('T') ? String(item.receptionDate).split('T')[0] : String(item.receptionDate)) : '';
        return d === selectedDateStr;
      })?.id;

      if (slotsPayload.length > 0) {
        const payload = {
          receptionDate: selectedDateStr,
          location: 'Phòng tiếp công dân',
          note: 'Lịch tiếp công dân định kỳ của Lãnh đạo',
          slots: slotsPayload
        };

        let res;
        if (existingId) {
          res = await apiClient.put(`/api/leader-meeting-schedules/management/${existingId}`, payload);
        } else {
          res = await apiClient.post('/api/leader-meeting-schedules/management', payload);
        }

        const savedData = res.data?.data || res.data;
        if (savedData) {
          setCurrentDaySchedule(savedData);
          const activeKeys = new Set();
          const bookedKeys = new Set();
          if (Array.isArray(savedData.slots)) {
            savedData.slots.forEach(s => {
              if (s.isActive !== false) {
                const dbStart = s.startTime || '09:00';
                const dbEnd = s.endTime || '10:30';
                const isSlotBooked = s.heldCount > 0 || (s.capacity && s.remainingCapacity !== undefined && s.remainingCapacity < s.capacity);
                ALL_STANDARD_SLOTS.forEach(std => {
                  if (std.startTime < dbEnd && std.endTime > dbStart) {
                    activeKeys.add(std.key);
                    if (isSlotBooked) bookedKeys.add(std.key);
                  }
                });
              }
            });
          }
          setSelectedSlotKeys(activeKeys);
          setBookedSlotKeys(bookedKeys);
        }

        showToast.success(isCurrentlyActive ? `Đã tắt ca ${slot.label}` : `Đã mở ca ${slot.label}`);
      } else {
        // Tắt hết tất cả các ca
        if (existingId) {
          try {
            await apiClient.delete(`/api/leader-meeting-schedules/management/${existingId}`);
          } catch (delErr) {
            await apiClient.put(`/api/leader-meeting-schedules/management/${existingId}/status`, { isActive: false });
          }
        }
        setCurrentDaySchedule(null);
        setSelectedSlotKeys(new Set());
        setBookedSlotKeys(new Set());
        showToast.info(`Đã đóng tất cả các ca trong ngày ${selectedDate.format('DD/MM/YYYY')}`);
      }

      // Tắt trạng thái xoay loading ngay lập tức
      setTogglingSlot(null);

      // Cập nhật danh sách ngày có lịch chạy ngầm ở background (không block UI)
      fetchSchedulesSummary();
    } catch (error) {
      console.error('Lỗi khi cập nhật ca:', error);
      showToast.error(error.response?.data?.message || 'Không thể cập nhật ca này');
      // Rollback lại
      setSelectedSlotKeys(selectedSlotKeys);
    } finally {
      setTogglingSlot(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* TIÊU ĐỀ TRANG */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
            <UserCheck className="w-4 h-4" />
            Lịch gặp lãnh đạo
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">
            Mở ca tiếp công dân
          </h1>
        </div>
      </div>

      {/* THANH CHỌN NGÀY VÀ THỐNG KÊ (NẰM GIỮA Ô MỞ CA VÀ Ô CA SÁNG) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-bold text-gray-700">Ngày làm việc:</span>
            </div>
            <DatePicker
              value={selectedDate}
              onChange={(d) => d && setSelectedDate(d)}
              format="DD/MM/YYYY"
              disabledDate={(current) => {
                if (!current) return false;
                const today = dayjs().startOf('day');
                const maxDate = dayjs().add(6, 'day').endOf('day');
                return current < today || current > maxDate;
              }}
              className="rounded-lg font-bold text-gray-800 h-9 min-w-[140px]"
              allowClear={false}
              cellRender={(current, info) => {
                if (info.type !== 'date') return info.originNode;
                const isPast = current < dayjs().startOf('day');
                const dateStr = current.format('YYYY-MM-DD');
                const hasSchedule = scheduledDateSet.has(dateStr);
                const isSelected = selectedDate && selectedDate.format('YYYY-MM-DD') === dateStr;
                
                if (hasSchedule && !isSelected && !isPast) {
                  return (
                    <div className="ant-picker-cell-inner !bg-blue-50 !text-blue-700 !font-bold !border !border-blue-200 rounded-md" title="Đã có ca mở tiếp dân">
                      {current.date()}
                    </div>
                  );
                }
                return info.originNode;
              }}
            />
            {selectedDate && (
              <span className="inline-flex items-center h-9 px-3 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                {getDayOfWeek(selectedDateStr)}
              </span>
            )}
          </div>

          <span className="inline-flex items-center h-9 text-xs font-medium text-gray-500 bg-gray-50 px-3 rounded-lg border border-gray-200">
            Đang mở: <b className="text-blue-600 font-bold ml-1 mr-1">{selectedSlotKeys.size} / 15</b> ca
          </span>
        </div>
      </div>

      {/* KHỐI BUỔI SÁNG (07:30 - 11:30) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs p-6 space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600">
              <Sun className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-bold text-sm text-gray-900 m-0 uppercase tracking-wide">
                Buổi sáng (07:30 - 11:30)
              </h2>
              <span className="text-[11px] text-gray-500 font-medium">
                8 ca làm việc theo giờ hành chính • 30 phút/ca
              </span>
            </div>
          </div>

          <span className="text-xs font-semibold px-3 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-100">
            {Array.from(selectedSlotKeys).filter(k => k.startsWith('07') || k.startsWith('08') || k.startsWith('09') || k.startsWith('10') || k.startsWith('11')).length} / 8 ca đang mở
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {STANDARD_MORNING_SLOTS.map(slot => {
            const isPassed = isSlotPassed(slot);
            const isActive = selectedSlotKeys.has(slot.key);
            const isBooked = bookedSlotKeys.has(slot.key);
            const isUpdatingThis = togglingSlot?.key === slot.key;
            const isOpeningThis = isUpdatingThis && togglingSlot?.action === 'OPENING';

            return (
              <div
                key={slot.key}
                onClick={() => !isPassed && !isUpdatingThis && handleToggleSlot(slot)}
                className={`flex items-center justify-between p-4 rounded-lg border text-left transition-all select-none ${
                  isPassed
                    ? 'bg-gray-100/70 border-gray-200 text-gray-400 opacity-50 grayscale cursor-not-allowed'
                    : isBooked || isActive
                      ? 'bg-blue-50/90 border-blue-300 text-blue-950 shadow-2xs ring-1 ring-blue-500 cursor-pointer'
                      : 'bg-gray-50/50 border-gray-200 text-gray-600 hover:bg-gray-100/70 hover:border-gray-300 cursor-pointer'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                    isPassed
                      ? 'bg-gray-200 text-gray-400'
                      : isBooked || isActive
                        ? 'bg-blue-600 text-white shadow-2xs'
                        : 'bg-gray-200 text-gray-400'
                  }`}>
                    {isBooked ? <Lock size={15} /> : <Clock size={15} />}
                  </div>
                  <div>
                    <div className="font-bold text-xs leading-tight">
                      {slot.label}
                    </div>
                    {isPassed ? (
                      <div className="text-[11px] mt-0.5 font-medium text-gray-400">
                        (Đã qua giờ)
                      </div>
                    ) : isUpdatingThis ? (
                      <div className="text-[11px] mt-0.5 font-bold text-blue-600 animate-pulse">
                        {isOpeningThis ? 'Đang mở ca...' : 'Đang tắt ca...'}
                      </div>
                    ) : isBooked ? (
                      <div className="text-[11px] mt-0.5 font-bold text-amber-700">
                        Đã có người đặt
                      </div>
                    ) : isActive ? (
                      <div className="text-[11px] mt-0.5 font-medium text-blue-700">
                        Chưa có người đặt
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    size="small"
                    disabled={isPassed}
                    checked={isActive || isBooked}
                    loading={isUpdatingThis}
                    onChange={() => !isPassed && handleToggleSlot(slot)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KHỐI BUỔI CHIỀU (13:30 - 17:00) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xs p-6 space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-200 flex items-center justify-center text-orange-600">
              <Sunset className="w-4 h-4" />
            </div>
            <div>
              <h2 className="font-bold text-sm text-gray-900 m-0 uppercase tracking-wide">
                Buổi chiều (13:30 - 17:00)
              </h2>
              <span className="text-[11px] text-gray-500 font-medium">
                7 ca làm việc theo giờ hành chính • 30 phút/ca
              </span>
            </div>
          </div>

          <span className="text-xs font-semibold px-3 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-100">
            {Array.from(selectedSlotKeys).filter(k => k.startsWith('13') || k.startsWith('14') || k.startsWith('15') || k.startsWith('16')).length} / 7 ca đang mở
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {STANDARD_AFTERNOON_SLOTS.map(slot => {
            const isPassed = isSlotPassed(slot);
            const isActive = selectedSlotKeys.has(slot.key);
            const isBooked = bookedSlotKeys.has(slot.key);
            const isUpdatingThis = togglingSlot?.key === slot.key;
            const isOpeningThis = isUpdatingThis && togglingSlot?.action === 'OPENING';

            return (
              <div
                key={slot.key}
                onClick={() => !isPassed && !isUpdatingThis && handleToggleSlot(slot)}
                className={`flex items-center justify-between p-4 rounded-lg border text-left transition-all select-none ${
                  isPassed
                    ? 'bg-gray-100/70 border-gray-200 text-gray-400 opacity-50 grayscale cursor-not-allowed'
                    : isBooked || isActive
                      ? 'bg-blue-50/90 border-blue-300 text-blue-950 shadow-2xs ring-1 ring-blue-500 cursor-pointer'
                      : 'bg-gray-50/50 border-gray-200 text-gray-600 hover:bg-gray-100/70 hover:border-gray-300 cursor-pointer'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                    isPassed
                      ? 'bg-gray-200 text-gray-400'
                      : isBooked || isActive
                        ? 'bg-blue-600 text-white shadow-2xs'
                        : 'bg-gray-200 text-gray-400'
                  }`}>
                    {isBooked ? <Lock size={15} /> : <Clock size={15} />}
                  </div>
                  <div>
                    <div className="font-bold text-xs leading-tight">
                      {slot.label}
                    </div>
                    {isPassed ? (
                      <div className="text-[11px] mt-0.5 font-medium text-gray-400">
                        (Đã qua giờ)
                      </div>
                    ) : isUpdatingThis ? (
                      <div className="text-[11px] mt-0.5 font-bold text-blue-600 animate-pulse">
                        {isOpeningThis ? 'Đang mở ca...' : 'Đang tắt ca...'}
                      </div>
                    ) : isBooked ? (
                      <div className="text-[11px] mt-0.5 font-bold text-amber-700">
                        Đã có người đặt
                      </div>
                    ) : isActive ? (
                      <div className="text-[11px] mt-0.5 font-medium text-blue-700">
                        Chưa có người đặt
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                  <Switch
                    size="small"
                    disabled={isPassed}
                    checked={isActive || isBooked}
                    loading={isUpdatingThis}
                    onChange={() => !isPassed && handleToggleSlot(slot)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
